/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    REMOVE_ADMIN_ACTIVITY_NAV
 * Date:         2026-01-05
 * Modified By:  Cursor
 *
 * Description:
 * Removed the Activity navigation item from the admin sidebar.
 *
 * File Size Check: (modifying existing file)
 * - Current: 152 lines
 * - Adding: ~18 lines (modification header)
 * - Projected: ~170 lines
 * - Decision: Proceed
 *
 * Related Screens / Modules:
 * - client/src/features/admin/activity/AdminActivityPage.tsx (deleted)
 */
import React from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import styles from "../../styles/admin-layout.module.scss";

interface BreadcrumbItem {
  to: string;
  label: string;
  isLast: boolean;
}

const AdminLayout: React.FC = () => {
  const location = useLocation();

  // Navigation items configuration (single source of truth)
  const adminNav = [
    {
      to: "/admin",
      label: "Dashboard",
      end: true,
    },
    { to: "/admin/users", label: "Users", end: false },
    { to: "/admin/inventory", label: "Inventory", end: false },
    {
      to: "/admin/scheduler/exchange-rates",
      label: "Exchange Rates",
      end: false,
    },
    // [2026-01-05] Admin activity: Removed Activity navigation item
    // [2025-12-20] Phase 3: Add Overview to admin nav
    // Overview nav item removed on 2026-01-05
    // [2025-12-20] Tracing: Add Monitoring to admin nav
    { to: "/admin/monitoring", label: "Monitoring", end: false },
    // [2025-01-04] Admin UI: Add scheduler navigation
    { to: "/admin/scheduler/backup", label: "Backups", end: false },
    { to: "/admin/scheduler/backup-files", label: "Backup Files", end: false },
    { to: "/admin/scheduler/jobs", label: "Scheduled Jobs", end: false },
    { to: "/admin/logs", label: "Logs", end: false },
    { to: "/admin/logs/mail", label: "Mail Logs", end: false },
    {
      to: "/admin/logins",
      label: "Logins / Sessions",
      end: false,
    },
    { to: "/admin/subscriptions", label: "Subscription Settings", end: false },
    // [2026-03-07] system-params: Add System Params nav item
    { to: "/admin/system-params", label: "System Params", end: false },
    // [2026-06-26] blocklist: Add Blocklist nav item
    { to: "/admin/blocklist", label: "Blocklist", end: false },
  ];

  // Breadcrumb generation
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const { pathname } = location;
    const segments = pathname.split("/").filter(Boolean); // ["admin", "users"]
    const crumbs: BreadcrumbItem[] = [];

    let currentPath = "";
    segments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === segments.length - 1;

      // Map path to label
      const navItem = adminNav.find((item) => item.to === currentPath);
      let label = navItem?.label || segment.charAt(0).toUpperCase() + segment.slice(1);

      // TODO: Handle nested IDs (e.g., /admin/users/123 -> Users > 123)
      if (!navItem && index > 0) {
        label = segment; // Show ID as-is for now
      }

      crumbs.push({
        to: currentPath,
        label,
        isLast,
      });
    });

    return crumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  return (
    <div className={styles["admin-layout"]}>
      {/* Admin Header */}
      <header className={`bg-white border-bottom mb-3 ${styles["admin-header"]}`}>
        {/* Breadcrumb */}
        <div className="px-3 py-2">
          <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
            <nav aria-label="breadcrumb" className={`${styles["admin-breadcrumb"]} flex-grow-1`}>
              <ol
                className={`breadcrumb mb-0 ${breadcrumbs.some((c) => c.isLast && c.label === "Dashboard") ? "" : "small"}`}
              >
                {breadcrumbs.map((crumb, i) => (
                  <li
                    key={i}
                    className={`breadcrumb-item ${crumb.isLast ? "active" : ""}`}
                    aria-current={crumb.isLast ? "page" : undefined}
                  >
                    {/* [2026-02-07] style-extraction: moved letterSpacing to admin-layout.module.scss */}
                    {crumb.isLast ? (
                      crumb.label === "Dashboard" ? (
                        <span className={`fw-700 text-color-black ${styles["dashboard-title"]}`}>
                          Dashboard
                        </span>
                      ) : (
                        crumb.label
                      )
                    ) : (
                      <Link to={crumb.to} className="text-decoration-none">
                        {crumb.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={styles["admin-main"]}>
        <div className={`bg-white ${styles["admin-content-area"]}`}>
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
